import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createUserThunk,
  getUsersThunk,
  updateUserThunk,
} from "../../store/actions/user.action";
import { ICreateUser } from "../../models/user.model";
import {
  Table as AntTable,
  Button as AntButton,
  Form,
  Input,
  Select,
  Modal as AntModal,
  Row,
  Col,
  Dropdown,
  Menu,
  message,
  App,
  Empty,
} from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import theme from "../../themes";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import ToastMessage from "../../utils/toastMessage";

// --- Types ---
interface User {
  _id: string;
  full_name: string;
  email: string;
  role: number;
  status: number;
  deleted_at?: Date | null;
  createdAt?: string;
}

// Pagination constants
const USERS_PER_PAGE = 15;

// --- Modal Component ---
const UserModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (form: ICreateUser) => void;
  editUser: User | null;
}> = ({ open, onClose, onSubmit, editUser }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editUser && open) {
      form.setFieldsValue({
        full_name: editUser.full_name,
        email: editUser.email,
        role: editUser.role,
      });
    } else if (open) {
      form.setFieldsValue({
        role: 2,
      });
    }
  }, [open, editUser, form]);

  const handleFormSubmit = async (values: any) => {
    setLoading(true);
    try {
      await onSubmit(values);
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <AntModal
      title={
        <ModalTitle>
          {editUser
            ? t("userManagement.editUser")
            : t("userManagement.addUser")}
        </ModalTitle>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={560}
      centered
      styles={{
        header: {
          borderBottom: "1px solid #f0f0f0",
        },
        body: {
          padding: "24px",
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormSubmit}
        requiredMark={false}
        initialValues={{
          role: 2,
        }}
      >
        <Form.Item
          name="full_name"
          label={t("userManagement.fullName")}
          rules={[
            {
              required: true,
              message: t("userManagement.validation.fullNameRequired"),
            },
            {
              max: 100,
              message: t("userManagement.validation.fullNameMaxLength"),
            },
          ]}
        >
          <Input placeholder={t("userManagement.enterFullName")} size="large" />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("userManagement.email")}
          rules={[
            {
              required: true,
              message: t("userManagement.validation.emailRequired"),
            },
            {
              type: "email",
              message: t("userManagement.validation.emailInvalid"),
            },
            {
              max: 100,
              message: t("userManagement.validation.emailMaxLength"),
            },
          ]}
        >
          <Input
            placeholder={t("userManagement.enterEmailAddress")}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="role"
          label={t("userManagement.role")}
          rules={[
            {
              required: true,
              message: t("userManagement.validation.roleRequired"),
            },
          ]}
        >
          <Select
            placeholder={t("userManagement.selectRole")}
            size="large"
            options={[
              { value: 1, label: t("userManagement.admin") },
              { value: 2, label: t("userManagement.campaignManager") },
            ]}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
          <Row gutter={16} justify="end">
            <Col>
              <AntButton
                size="large"
                onClick={handleCancel}
                style={{ minWidth: 100 }}
              >
                {t("userManagement.cancel")}
              </AntButton>
            </Col>
            <Col>
              <CreateButton type="submit">
                {editUser
                  ? t("userManagement.saveChanges")
                  : t("userManagement.createUser")}
              </CreateButton>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </AntModal>
  );
};

// --- Main Component ---
const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [statusFilter, setStatusFilter] = useState<number>(1);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const dispatch = useAppDispatch();
  const { users, loading, error } = useAppSelector((state) => state.user);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  // USER_STATUS with translations
  const USER_STATUS = {
    ACTIVE: {
      label: t("userManagement.active"),
      color: theme.colors.green1,
      value: 1,
    },
    INVITED: {
      label: t("userManagement.invited"),
      color: theme.colors.blue1,
      value: 2,
    },
    EXPIRED: {
      label: t("userManagement.expired"),
      color: theme.colors.grey1,
      value: 3,
    },
    SUSPENDED: {
      label: t("userManagement.suspended"),
      color: theme.colors.grey1,
      value: 4,
    },
  };

  // Numeric status enum mapping used by API
  const STATUS_ENUM: Record<
    "active" | "invited" | "expired" | "suspended",
    number
  > = {
    active: 1,
    invited: 2,
    expired: 3,
    suspended: 4,
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to first page on new search
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchUsers = async () => {
    await dispatch(
      getUsersThunk({
        page,
        limit: USERS_PER_PAGE,
        status: statusFilter,
        keyword: search,
      })
    ).unwrap();
  };

  const openCreate = () => {
    setEditUser(null);
    setModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditUser(null);
  };

  const handleSubmit = async (formData: ICreateUser) => {
    if (editUser) {
      // Update existing user
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
      };
      try {
        await dispatch(
          updateUserThunk({ id: editUser._id, payload: updateData })
        ).unwrap();
        toast.success(
          t("userManagement.userUpdatedSuccessfully"),
          5,
          <CheckCircleIcon />
        );
        await fetchUsers();
      } catch (error: any) {
        toast.error(error || t("userManagement.failedToUpdateUser"), 5);
      }
    } else {
      try {
        await dispatch(createUserThunk(formData)).unwrap();
        toast.success(
          t("userManagement.userCreatedSuccessfully"),
          5,
          <CheckCircleIcon />
        );
        await fetchUsers();
      } catch (error: any) {
        toast.error(error || t("userManagement.failedToCreateUser"));
      }
    }
    closeModal();
  };

  const handleSuspend = async (user: User) => {
    try {
      await dispatch(
        updateUserThunk({
          id: user._id,
          payload: { deleted_at: user.deleted_at ? null : new Date() },
        })
      ).unwrap();
      toast.success(
        getStatus(user) === t("userManagement.active")
          ? t("userManagement.userSuspendedSuccessfully")
          : t("userManagement.userActivatedSuccessfully")
      );
      await fetchUsers();
    } catch (error: any) {
      toast.error(error || t("userManagement.failedToSuspendUser"));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [statusFilter, page, search]);

  // Status logic
  const getStatus = (user: User) => {
    if (user.deleted_at) return USER_STATUS.SUSPENDED.label;
    if (user.status === USER_STATUS.ACTIVE.value)
      return USER_STATUS.ACTIVE.label;
    if (user.status === USER_STATUS.INVITED.value)
      return USER_STATUS.INVITED.label;
    if (user.status === USER_STATUS.EXPIRED.value)
      return USER_STATUS.EXPIRED.label;

    return USER_STATUS.EXPIRED.label;
  };

  // Define columns for Ant Design Table
  const columns: ColumnsType<User> = [
    {
      title: t("userManagement.userName"),
      dataIndex: "full_name",
      key: "full_name",
      width: "20%",
      ellipsis: true,
    },
    {
      title: t("userManagement.email"),
      dataIndex: "email",
      key: "email",
      width: "25%",
      ellipsis: true,
    },
    {
      title: t("userManagement.userType"),
      dataIndex: "role",
      key: "role",
      width: "15%",
      render: (role: number) => {
        const roleObj = [
          { value: 1, label: t("userManagement.admin") },
          { value: 2, label: t("userManagement.campaignManager") },
        ].find((r) => r.value === role);
        return roleObj ? roleObj.label : t("userManagement.unknown");
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      render: (_: any, record: User) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  label: t("userManagement.editUserAction"),
                  onClick: () => openEdit(record),
                },
                {
                  key: "delete",
                  label:
                    getStatus(record) !== USER_STATUS.SUSPENDED.label
                      ? t("userManagement.suspendUserAction")
                      : t("userManagement.activateUserAction"),
                  onClick: () => handleSuspend(record),
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <AntButton type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Container>
      <Header>
        <TitleContainer>
          <Title>{t("userManagement.title")}</Title>
        </TitleContainer>
        <CreateButton onClick={openCreate}>
          {t("userManagement.addUser")}
        </CreateButton>
      </Header>
      <TableContainer>
        <TopBar>
          <SortSelect
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val as number);
              setPage(1);
            }}
            size="large"
            style={{ width: 260 }}
            options={[
              {
                value: STATUS_ENUM.active,
                label: t("userManagement.activeUsers"),
              },
              {
                value: STATUS_ENUM.suspended,
                label: t("userManagement.suspendedUsers"),
              },
              {
                value: STATUS_ENUM.invited,
                label: t("userManagement.invitedUsers"),
              },
            ]}
          />
          <TopBarRight>
            <SearchWrapper>
              <CustomSearch
                placeholder={t("userManagement.searchUser")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </SearchWrapper>
          </TopBarRight>
        </TopBar>
        <StyledTable
          columns={columns as any}
          dataSource={users.data}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: page,
            total: users.pagination.totalItems,
            pageSize: USERS_PER_PAGE,
            showSizeChanger: false,
            onChange: (page) => setPage(page),
            position: ["bottomCenter"],
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                }
              />
            ),
          }}
        />
      </TableContainer>
      <UserModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        editUser={editUser}
      />
    </Container>
  );
};

// --- Styled Components ---
const Container = styled.div`
  height: 100svh;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.grey3};
  padding: 52px 72px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  min-height: 36px;
  gap: 16px;
`;

const TitleContainer = styled.div`
  color: #1c1c1e;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  background: linear-gradient(135deg, #1e293b, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const CreateButton = styled.button`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const TableContainer = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  padding: 32px 32px 16px 32px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent container overflow */

  @media (max-width: 768px) {
    padding: 16px 16px 8px 16px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 12px 8px 12px;
    border-radius: 8px;
  }

  /* Override Ant Design table wrapper */
  .ant-table-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-spin-nested-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-spin-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-table {
    flex: 1;
    overflow: auto; /* Enable scrolling for table content */
  }

  .ant-table-body {
    overflow-y: auto !important; /* Force vertical scroll */
    max-height: none !important; /* Remove max-height restriction */
  }

  .ant-pagination {
    margin: 0;
    flex-shrink: 0; /* Prevent pagination from shrinking */
  }

  /* Mobile table overrides */
  @media (max-width: 768px) {
    .ant-table-thead > tr > th {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }

    .ant-table-tbody > tr > td {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }

    .ant-table-cell {
      word-break: break-word;
    }
  }

  @media (max-width: 480px) {
    .ant-table-thead > tr > th {
      padding: 6px 2px !important;
      font-size: 11px !important;
    }

    .ant-table-tbody > tr > td {
      padding: 6px 2px !important;
      font-size: 11px !important;
    }

    .ant-btn {
      padding: 4px 6px !important;
      font-size: 11px !important;
    }

    .ant-btn .anticon {
      font-size: 10px !important;
    }
  }
`;

const ModalTitle = styled.h3`
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.8rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  width: 100%;
  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }
  .ant-tabs-nav::before {
    border-bottom: none !important;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SortSelect = styled(Select)`
  .ant-select-selector {
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .ant-select-selector:focus,
  .ant-select-selector:active {
    box-shadow: none !important;
    outline: none !important;
  }
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

// Fix StyledTable generic type
const StyledTable = styled(AntTable)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }
  .ant-table-thead > tr > th::before {
    display: none;
  }
  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }
  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }
  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }
  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }
  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default UserManagement;
